import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { dialerOptions, useDialers } from '@/lib/dialers';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  Modal,
  PageHeader,
  Select,
  Toast,
  cx,
} from '@/components/ui';

interface AssignmentSummary {
  assignment_id: string;
  bot_id: string;
  bot_name: string;
  bot_code: string;
  campaign: string | null;
  in_group: string | null;
  enabled: boolean;
  priority: number;
}

interface MatrixUser {
  vicidial_user_id: string;
  vicidial_user: string;
  name: string;
  campaign: string | null;
  in_group: string | null;
  enabled: boolean;
  assigned_bot_id: string | null;
  assignments: AssignmentSummary[];
}

interface MatrixBot {
  id: string;
  name: string;
  code: string;
  /** 'AI' or 'SOUNDBOARD'. Both are assignable and they behave nothing alike. */
  kind: string;
  bot_group_id: string | null;
  status: string;
}

/** A soundboard said so, everywhere a bot is offered as a choice.
 *
 * Two bots called "Roofing" — one holding a conversation, one playing seven
 * recordings — are indistinguishable in a dropdown without this, and the
 * mistake is only visible afterwards, on a live call. */
function botLabel(bot: { name: string; code: string; kind?: string }): string {
  return bot.kind === 'SOUNDBOARD'
    ? `${bot.name} (${bot.code}) — soundboard`
    : `${bot.name} (${bot.code})`;
}

interface Matrix {
  users: MatrixUser[];
  bots: MatrixBot[];
}

interface Preview {
  vicidial_user: string;
  campaign: string | null;
  bot_id: string | null;
  bot_name: string | null;
  matched_by: string;
  integration_id: string | null;
  integration_name: string | null;
  resolved: boolean;
}

const MATCH_LABELS: Record<string, string> = {
  assignment_campaign_ingroup: 'Assignment · campaign + in-group',
  assignment_campaign: 'Assignment · campaign',
  assignment_user: 'Assignment · user',
  user_primary_bot: 'Primary bot pointer',
  campaign_default: 'Campaign default bot',
  none: 'No bot would answer',
};

export default function Assignments() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['assignment-matrix'],
    queryFn: () => api.get<Matrix>('/bot-assignments/matrix'),
  });

  const remove = useMutation({
    mutationFn: (assignmentId: string) => api.delete(`/bot-assignments/${assignmentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment-matrix'] });
      setToast({ message: 'Assignment removed', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not remove'),
        tone: 'bad',
      }),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/bot-assignments/${id}`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assignment-matrix'] }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const { users, bots } = data;
  const canEdit = can('bot_assignments:update');
  const unassigned = users.filter(
    (u) => u.assignments.length === 0 && !u.assigned_bot_id,
  ).length;

  return (
    <div className="w-full">
      <PageHeader
        title="Assignments"
        description="Which Glee AI Bot answers for each Vicidial user."
        actions={
          can('bot_assignments:create') && (
            <Button onClick={() => setBulkOpen(true)}>Bulk assign</Button>
          )
        }
      />

      {unassigned > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
          <strong>{unassigned}</strong> Vicidial user{unassigned === 1 ? ' has' : 's have'} no
          bot assigned. Calls from {unassigned === 1 ? 'that user' : 'those users'} will be
          rejected with <code className="font-mono text-xs">no_bot_assigned</code>.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card
            title="Vicidial user → Glee AI Bot"
            description={`${users.length} users · ${bots.length} bots`}
            padded={false}
          >
            {users.length === 0 ? (
              <EmptyState
                title="No Vicidial users yet"
                description="Import them from Vicidial in Settings, or add them manually."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="glee-table">
                  <thead>
                    <tr>
                      <th>
                        Vicidial user
                      </th>
                      <th>
                        Assigned bots
                      </th>
                      <th className="text-right">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.vicidial_user_id}>
                        <td className="py-3 align-top">
                          <div className="font-mono text-sm text-ink-900 dark:text-ink-50">
                            {user.vicidial_user}
                          </div>
                          <div className="text-xs text-ink-500 dark:text-ink-400">
                            {user.name}
                            {user.campaign && ` · ${user.campaign}`}
                            {user.in_group && ` · ${user.in_group}`}
                          </div>
                          {!user.enabled && (
                            <Badge tone="gray" className="mt-1">
                              Disabled
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 align-top">
                          {user.assignments.length === 0 ? (
                            user.assigned_bot_id ? (
                              <Badge tone="blue">Primary bot pointer only</Badge>
                            ) : (
                              <Badge tone="amber">Unassigned</Badge>
                            )
                          ) : (
                            <ul className="space-y-1">
                              {user.assignments.map((assignment) => (
                                <li
                                  key={assignment.assignment_id}
                                  className="flex flex-wrap items-center gap-1.5"
                                >
                                  <Badge tone={assignment.enabled ? 'green' : 'gray'}>
                                    {assignment.bot_name}
                                  </Badge>
                                  {assignment.campaign && (
                                    <span className="text-xs text-ink-500">
                                      {assignment.campaign}
                                      {assignment.in_group && ` / ${assignment.in_group}`}
                                    </span>
                                  )}
                                  {canEdit && (
                                    <span className="flex gap-1">
                                      <button
                                        onClick={() =>
                                          toggle.mutate({
                                            id: assignment.assignment_id,
                                            enabled: !assignment.enabled,
                                          })
                                        }
                                        className="text-xs text-ink-400 hover:text-brand-600"
                                      >
                                        {assignment.enabled ? 'disable' : 'enable'}
                                      </button>
                                      <button
                                        onClick={() => remove.mutate(assignment.assignment_id)}
                                        className="text-xs text-ink-400 hover:text-red-600"
                                      >
                                        remove
                                      </button>
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="py-3 text-right align-top">
                          {canEdit && (
                            <AssignButton
                              user={user}
                              bots={bots}
                              onDone={() => {
                                queryClient.invalidateQueries({ queryKey: ['assignment-matrix'] });
                                setToast({ message: 'Assignment saved', tone: 'good' });
                              }}
                              onError={(message) => setToast({ message, tone: 'bad' })}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <ResolutionPreview users={users} />
      </div>

      {bulkOpen && (
        <BulkAssignModal
          users={users}
          bots={bots}
          onClose={() => setBulkOpen(false)}
          onDone={(created, updated) => {
            setBulkOpen(false);
            queryClient.invalidateQueries({ queryKey: ['assignment-matrix'] });
            setToast({
              message: `${created} created, ${updated} updated`,
              tone: 'good',
            });
          }}
          onError={(message) => setToast({ message, tone: 'bad' })}
        />
      )}

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

function AssignButton({
  user,
  bots,
  onDone,
  onError,
}: {
  user: MatrixUser;
  bots: MatrixBot[];
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [botId, setBotId] = useState('');
  const [campaign, setCampaign] = useState(user.campaign ?? '');
  const [inGroup, setInGroup] = useState(user.in_group ?? '');

  const create = useMutation({
    mutationFn: () =>
      api.post('/bot-assignments', {
        vicidial_user_id: user.vicidial_user_id,
        bot_id: botId,
        campaign: campaign || null,
        in_group: inGroup || null,
        enabled: true,
        priority: 100,
      }),
    onSuccess: () => {
      setOpen(false);
      setBotId('');
      onDone();
    },
    onError: (err) =>
      onError(errorMessage(err, 'Could not create assignment')),
  });

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        Assign
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Assign a bot to ${user.vicidial_user}`}
        description="Leave campaign and in-group blank to match any call from this user."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!botId}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Glee AI Bot" required>
            <Select value={botId} onChange={(e) => setBotId(e.target.value)}>
              <option value="">Select a bot…</option>
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {botLabel(bot)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Campaign" hint="Optional — restricts this assignment to one campaign.">
            <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="HOME01" />
          </Field>
          <Field label="In-group" hint="Optional — restricts further to one in-group.">
            <Input value={inGroup} onChange={(e) => setInGroup(e.target.value)} placeholder="VERIFIER01" />
          </Field>
        </div>
      </Modal>
    </>
  );
}

function BulkAssignModal({
  users,
  bots,
  onClose,
  onDone,
  onError,
}: {
  users: MatrixUser[];
  bots: MatrixBot[];
  onClose: () => void;
  onDone: (created: number, updated: number) => void;
  onError: (message: string) => void;
}) {
  const [strategy, setStrategy] = useState<'pairwise' | 'fanout' | 'single_bot'>('pairwise');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [campaign, setCampaign] = useState('');
  const [inGroup, setInGroup] = useState('');
  const [replace, setReplace] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ created: number; updated: number }>('/bot-assignments/bulk', {
        vicidial_user_ids: selectedUsers,
        bot_ids: selectedBots,
        strategy,
        campaign: campaign || null,
        in_group: inGroup || null,
        enabled: true,
        priority: 100,
        set_primary_bot: true,
        replace_existing: replace,
      }),
    onSuccess: (result) => onDone(result.created, result.updated),
    onError: (err) =>
      onError(errorMessage(err, 'Bulk assignment failed')),
  });

  const mismatch =
    strategy === 'pairwise' && selectedUsers.length !== selectedBots.length;
  const singleBotProblem = strategy === 'single_bot' && selectedBots.length !== 1;
  const canSubmit =
    selectedUsers.length > 0 && selectedBots.length > 0 && !mismatch && !singleBotProblem;

  const pairCount = useMemo(() => {
    if (strategy === 'pairwise') return Math.min(selectedUsers.length, selectedBots.length);
    if (strategy === 'single_bot') return selectedUsers.length;
    return selectedUsers.length * selectedBots.length;
  }, [strategy, selectedUsers.length, selectedBots.length]);

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Bulk assign"
      description="Map many Vicidial users onto bots in one step."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Assign {pairCount > 0 ? `${pairCount} pair${pairCount === 1 ? '' : 's'}` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Strategy"
          hint={
            strategy === 'pairwise'
              ? 'Position by position: the 1st user gets the 1st bot, and so on. Requires equal counts.'
              : strategy === 'single_bot'
                ? 'Every selected user points at one shared bot.'
                : 'Every selected user is assigned to every selected bot.'
          }
        >
          <Select
            value={strategy}
            onChange={(e) => setStrategy(e.target.value as typeof strategy)}
          >
            <option value="pairwise">Pairwise (10 users → 10 bots)</option>
            <option value="single_bot">All users → one bot</option>
            <option value="fanout">Every user → every bot</option>
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <PickList
            label={`Vicidial users (${selectedUsers.length})`}
            items={users.map((u) => ({
              id: u.vicidial_user_id,
              primary: u.vicidial_user,
              secondary: u.name,
            }))}
            selected={selectedUsers}
            onChange={setSelectedUsers}
          />
          <PickList
            label={`Bots (${selectedBots.length})`}
            items={bots.map((b) => ({
              id: b.id,
              primary: b.name,
              secondary: b.kind === 'SOUNDBOARD' ? `${b.code} — soundboard` : b.code,
            }))}
            selected={selectedBots}
            onChange={setSelectedBots}
          />
        </div>

        {mismatch && (
          <p className="text-xs text-red-600">
            Pairwise needs the same number of users and bots — {selectedUsers.length} vs{' '}
            {selectedBots.length}.
          </p>
        )}
        {singleBotProblem && (
          <p className="text-xs text-red-600">Select exactly one bot for this strategy.</p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Campaign" hint="Optional">
            <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="HOME01" />
          </Field>
          <Field label="In-group" hint="Optional">
            <Input value={inGroup} onChange={(e) => setInGroup(e.target.value)} placeholder="VERIFIER01" />
          </Field>
        </div>

        <label className="flex items-start gap-2 text-sm text-ink-700 dark:text-ink-200">
          <input
            type="checkbox"
            checked={replace}
            onChange={(e) => setReplace(e.target.checked)}
            className="mt-0.5 rounded border-ink-300"
          />
          <span>
            Replace existing assignments in this scope
            <span className="block text-xs text-ink-500">
              Removes the selected users&apos; current assignments for the same campaign and
              in-group before creating the new ones.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

function PickList({
  label,
  items,
  selected,
  onChange,
}: {
  label: string;
  items: Array<{ id: string; primary: string; secondary?: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium text-ink-700 dark:text-ink-200">{label}</span>
        <button
          type="button"
          onClick={() => onChange(allSelected ? [] : items.map((i) => i.id))}
          className="text-xs text-brand-600 hover:underline"
        >
          {allSelected ? 'Clear' : 'Select all'}
        </button>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-md ring-1 ring-inset ring-ink-200 dark:ring-ink-700">
        {items.length === 0 ? (
          <p className="p-3 text-xs text-ink-500">Nothing to select.</p>
        ) : (
          <ul className="divide-y divide-line">
            {items.map((item) => {
              const checked = selected.includes(item.id);
              return (
                <li key={item.id}>
                  <label
                    className={cx(
                      'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                      checked
                        ? 'bg-brand-50 dark:bg-ink-800'
                        : 'hover:bg-ink-50 dark:hover:bg-ink-800/60',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        onChange(
                          checked
                            ? selected.filter((id) => id !== item.id)
                            : [...selected, item.id],
                        )
                      }
                      className="rounded border-ink-300"
                    />
                    <span className="truncate font-mono text-xs">{item.primary}</span>
                    {item.secondary && (
                      <span className="truncate text-xs text-ink-500">{item.secondary}</span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="mt-1 text-[11px] text-ink-400">Order of selection sets pairwise order.</p>
    </div>
  );
}

function ResolutionPreview({ users }: { users: MatrixUser[] }) {
  const [vicidialUser, setVicidialUser] = useState('');
  const [campaign, setCampaign] = useState('');
  const [inGroup, setInGroup] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [result, setResult] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: dialers } = useDialers();
  const servers = dialerOptions(dialers?.data);

  const run = useMutation({
    mutationFn: () =>
      api.get<Preview>('/bot-assignments/preview', {
        vicidial_user: vicidialUser,
        campaign: campaign || undefined,
        in_group: inGroup || undefined,
        integration_id: integrationId || undefined,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err) => {
      setResult(null);
      setError(errorMessage(err, 'Preview failed'));
    },
  });

  return (
    <Card
      title="Resolution preview"
      description="Which bot would answer, and why."
    >
      <div className="space-y-3">
        {servers.length > 0 && (
          <Field
            label="Vicidial server"
            hint="The same username can exist on more than one dialer, pointing at a different bot."
          >
            <Select value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}>
              <option value="">Any server</option>
              {servers.map((server) => (
                <option key={server.value} value={server.value}>
                  {server.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Vicidial user" required>
          <Select value={vicidialUser} onChange={(e) => setVicidialUser(e.target.value)}>
            <option value="">Select…</option>
            {users.map((u) => (
              <option key={u.vicidial_user_id} value={u.vicidial_user}>
                {u.vicidial_user}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Campaign">
            <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="HOME01" />
          </Field>
          <Field label="In-group">
            <Input value={inGroup} onChange={(e) => setInGroup(e.target.value)} placeholder="VERIFIER01" />
          </Field>
        </div>
        <Button
          className="w-full"
          disabled={!vicidialUser}
          loading={run.isPending}
          onClick={() => run.mutate()}
        >
          Preview
        </Button>

        {error && <p className="text-xs text-red-600">{error}</p>}

        {result && (
          <div
            className={cx(
              'rounded-md p-3 text-sm ring-1 ring-inset',
              result.resolved
                ? 'bg-emerald-50 ring-emerald-200 dark:bg-emerald-950/40 dark:ring-emerald-900'
                : 'bg-red-50 ring-red-200 dark:bg-red-950/40 dark:ring-red-900',
            )}
          >
            <p className="font-medium text-ink-900 dark:text-ink-50">
              {result.resolved ? result.bot_name : 'No bot would answer'}
            </p>
            <p className="mt-0.5 text-xs text-ink-600 dark:text-ink-300">
              {MATCH_LABELS[result.matched_by] ?? result.matched_by}
            </p>
            {result.resolved && servers.length > 0 && (
              <p className="mt-1 text-xs text-ink-600 dark:text-ink-300">
                Dispositions go back to{' '}
                <strong>{result.integration_name ?? 'no server — write-backs unrouted'}</strong>
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
